import { defineMessages } from "react-intl";

export type OAuthErrorMessage = keyof typeof OAUTH_ERROR_MESSAGES;

export const MESSAGES = defineMessages({
  ecommerceDataUsage: {
    id: "funding-application: connect ecommerce data usage",
    defaultMessage:
      "We use your {platform} data to determine how much funding we can offer. This data gives us important information about the performance of your business, and insights into how your business is likely to grow.",
  },
  paymentPlatformDataUsage: {
    id: "funding-application: connect payment platform data usage",
    defaultMessage:
      "Your payment platform data is used alongside the data from your eCommerce platform to give us important information about your revenue and order patterns, and insights into how your company is likely to grow. This helps us determine how much funding we can offer.",
  },
  accountingPlatformDataUsage: {
    id: "funding-application: connect accounting platform data usage",
    defaultMessage:
      "We use your accounting data to understand your company’s profits, losses and balance over time. Together with your banking data, this gives us important information about the overall financial health of your business and helps to inform our funding decision.",
  },
  marketingDescription: {
    id: "funding-application: connect marketing and analytics description",
    defaultMessage:
      "Your marketing and analytics data helps us understand the return on your advertising investment, and whether your growth is sustainable. This information forms an important part of our financial assessment.",
    description: "Describes the way Wayflyer will use the customers marketing and analytics data",
  },
  cancelLabel: { id: "funding-application: Cancel", defaultMessage: "Cancel" },
  connectLabel: { id: "funding-application: Connect", defaultMessage: "Connect" },
  connectStoreLabel: { id: "funding-application: Connect store", defaultMessage: "Connect store" },
  connectAnotherStore: {
    id: "funding-application: shopify connect another store",
    defaultMessage: "Connect <link>another store</link>.",
  },
  connectAnotherAccount: {
    id: "funding-application: connect another account",
    defaultMessage: "Connect <link>another account</link>.",
  },
  multipleAccountsHelperTitle: {
    id: "funding-application: shopify multiple accounts helper title",
    defaultMessage: "Got multiple Shopify accounts?",
  },
  multipleAccountsHelperBody: {
    id: "funding-application: shopify multiple accounts helper body",
    defaultMessage: "Our <link>Help Center</link> shows you how to connect them all.",
  },
  oauthErrorBannerTitle: { id: "funding-application: oauth error banner title", defaultMessage: "Unable to connect" },
  oauthErrorBannerWeEncountered: {
    id: "funding-application: oauth error banner we encountered",
    defaultMessage: "We encountered an error while trying to connect.",
  },
  oauthErrorBannerTryAgain: {
    id: "funding-application: oauth error banner try again",
    defaultMessage: "Please try again.",
  },
  pleaseWait: {
    id: "funding-application: please wait",
    defaultMessage: "Please wait",
  },
  pullingAccounts: {
    id: "funding-application: pulling accounts",
    defaultMessage: "We're pulling your {platform} accounts. It'll just take a moment.",
  },
  selectAccounts: {
    id: "funding-application: select accounts",
    defaultMessage: "Select accounts associated with your store to include in your application.",
    description: "Ask users to select the accounts of their marketing platform they want to grant access to",
  },
});

export const OAUTH_ERROR_MESSAGES = defineMessages({
  access_denied: {
    id: "funding-application: reconnection - denied access",
    defaultMessage: "{dataSourceName} denied access.",
  },
  no_accounts: {
    id: "funding-application: reconnection - no accounts",
    defaultMessage: "{dataSourceName} has no accounts available for connection.",
  },
  missing_parameters: {
    id: "funding-application: reconnection - missing permissions",
    defaultMessage: "{dataSourceName} account does not have access to any data.",
  },
  invalid_facebook_configuration: {
    id: "funding-application: invalid_facebook_configuration",
    defaultMessage: "Unable to connect. Please contact support.",
  },
  tiktok_lost_access_to_accounts: {
    id: "funding-application: tiktok_lost_access_to_accounts",
    defaultMessage: "Unable to reconnect. Lost access to some of the previously authorized accounts.",
  },
  two_step_verification_not_enrolled: {
    id: "funding-application: reconnection - two step authentication not enrolled",
    defaultMessage: "{dataSourceName} admin requires two-step verification.",
  },
  shop_uniqueness_violation: {
    id: "funding-application: shop uniqueness violation exception",
    defaultMessage: "This connection already exists. Please connect a different store.",
  },
  amazon_shop_uniqueness_violation: {
    id: "funding-application: amazon shop uniqueness violation exception",
    defaultMessage:
      "This connection already exists. Please connect a different store. Note: Amazon stores are identified on a region level (North America, Europe, etc.).",
  },
  shop_reconnection_difference: {
    id: "funding-application: shop reconnection difference exception",
    defaultMessage: "Reconnecting with a different store is not allowed. Please connect the original store.",
  },
  unsupported_currency: {
    id: "funding-application: shopify connection error currency",
    defaultMessage: "We do not currently support your store currency.",
  },
  tiktok_insufficient_scope: {
    id: "funding-application: tiktok insufficient scope",
    defaultMessage: "Insufficient permissions. Please reconnect selecting all of the requested permissions.",
  },
  tiktok_reconnect_error: {
    id: "funding-application: unable to complete tiktok reconnection",
    defaultMessage: "Unable to complete {dataSourceName} reconnection.",
  },
  bigcommerce_single_click_exception: {
    id: "funding-application: unable to complete bigcommerce singe-click connection",
    defaultMessage:
      "Error while trying to establish connection. Please try to re-install the application from the BigCommerce dashboard.",
  },
  invalid_bigcommerce_access_token_url: {
    id: "funding-application: valid bigcommerce access token hint",
    defaultMessage: "Please enter a valid access token for this store.",
  },
  // This case covers stripe, woocommerce, magento and other
  default: {
    id: "funding-application: unable to complete connection",
    defaultMessage: "Unable to complete {dataSourceName} connection.",
  },
});
